import { useState, useMemo } from 'react'
import { fmt } from '../lib/constants'
import { createLead, updateLead, deleteLead } from '../lib/supabase'
import LeadTimeline from './LeadTimeline'
import { isLeadConverted } from '../lib/leadConversion'

const LEAD_STATUSES = ['New Lead', 'Contacted', 'Meeting Scheduled', 'Proposal Sent', 'Negotiation', 'Won', 'Lost']
const STATUS_COLORS = {
  'New Lead': { color: '#6b7280', bg: 'rgba(107,114,128,0.12)', dot: '#6b7280' },
  'Contacted': { color: '#3b82f6', bg: 'rgba(59,130,246,0.12)', dot: '#3b82f6' },
  'Meeting Scheduled': { color: '#8b5cf6', bg: 'rgba(139,92,246,0.12)', dot: '#8b5cf6' },
  'Proposal Sent': { color: '#f59e0b', bg: 'rgba(245,158,11,0.12)', dot: '#f59e0b' },
  'Negotiation': { color: '#f97316', bg: 'rgba(249,115,22,0.12)', dot: '#f97316' },
  'Won': { color: '#10b981', bg: 'rgba(16,185,129,0.12)', dot: '#10b981' },
  'Lost': { color: '#ef4444', bg: 'rgba(239,68,68,0.12)', dot: '#ef4444' },
}
const PRIORITIES = ['hot', 'warm', 'cold']

function timeAgo(dateStr) {
  if (!dateStr) return ''
  const diff = (Date.now() - new Date(dateStr)) / 1000
  if (diff < 86400) return 'today'
  const days = Math.floor(diff / 86400)
  if (days === 1) return 'yesterday'
  if (days < 30) return `${days}d ago`
  return `${Math.floor(days / 30)}mo ago`
}

function daysUntil(dateStr) {
  if (!dateStr) return null
  const diff = Math.ceil((new Date(dateStr) - Date.now()) / 86400000)
  if (diff < 0) return { text: `${Math.abs(diff)}d overdue`, overdue: true }
  if (diff === 0) return { text: 'Today', overdue: false }
  if (diff === 1) return { text: 'Tomorrow', overdue: false }
  return { text: `in ${diff}d`, overdue: false }
}

const emptyLead = { brand: '', client: '', contact_name: '', contact_email: '', contact_phone: '', status: 'New Lead', priority: 'warm', estimated_value: 0, notes: '', next_follow_up: '', last_contacted: '' }

export default function PersonalCRM({ leads, onRefresh, currentUser, isMobile, privacyMode, ownerLabel, onConvertToDeal, onCopyLeadToMyCRM, deals }) {
  const [view, setView] = useState('list')
  const [sortField, setSortField] = useState('created_at')
  const [sortDir, setSortDir] = useState('desc')
  const [search, setSearch] = useState('')
  const [filterStatus, setFilterStatus] = useState('Active')
  const [showAddForm, setShowAddForm] = useState(false)
  const [editingLead, setEditingLead] = useState(null)
  const [form, setForm] = useState({ ...emptyLead })
  const [saving, setSaving] = useState(false)
  const [expandedLead, setExpandedLead] = useState(null)
  const hide = privacyMode

  const filtered = useMemo(() => {
    let l = leads
    if (search) {
      const q = search.toLowerCase()
      l = l.filter(x => [x.brand, x.client, x.contact_name, x.contact_email, x.notes].some(f => f && f.toLowerCase().includes(q)))
    }
    if (filterStatus === 'Active') l = l.filter(x => x.status !== 'Won' && x.status !== 'Lost')
    if (filterStatus === 'Won') l = l.filter(x => x.status === 'Won')
    if (filterStatus === 'Lost') l = l.filter(x => x.status === 'Lost')
    return l
  }, [leads, search, filterStatus])

  // Sorted for list view
  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      let va = a[sortField] ?? '', vb = b[sortField] ?? ''
      if (typeof va === 'number') return sortDir === 'asc' ? va - vb : vb - va
      return sortDir === 'asc' ? String(va).localeCompare(String(vb)) : String(vb).localeCompare(String(va))
    })
  }, [filtered, sortField, sortDir])

  // Board view: group by status
  const boardGroups = useMemo(() => {
    const statuses = filterStatus === 'Active'
      ? LEAD_STATUSES.filter(s => s !== 'Won' && s !== 'Lost')
      : filterStatus === 'Won' ? ['Won']
      : filterStatus === 'Lost' ? ['Lost']
      : LEAD_STATUSES
    return statuses.map(s => ({
      status: s,
      leads: filtered.filter(l => l.status === s).sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''))
    })).filter(g => g.leads.length > 0 || (filterStatus === 'Active' && !['Won', 'Lost'].includes(g.status)))
  }, [filtered, filterStatus])

  const totalValue = filtered.reduce((s, l) => s + (l.estimated_value || 0), 0)
  const activeCount = leads.filter(l => l.status !== 'Won' && l.status !== 'Lost').length
  const wonCount = leads.filter(l => l.status === 'Won').length
  const overdueCount = leads.filter(l => {
    if (!l.next_follow_up || l.status === 'Won' || l.status === 'Lost') return false
    return new Date(l.next_follow_up) < new Date()
  }).length

  const handleSave = async () => {
    setSaving(true)
    const payload = { ...form, estimated_value: Number(form.estimated_value) || 0, next_follow_up: form.next_follow_up || null }
    if (editingLead) {
      const { error } = await updateLead(editingLead.id, payload)
      if (error) { console.error('Update failed:', error); alert('Update failed — ' + error.message); setSaving(false); return }
    } else {
      const { error } = await createLead({
        ...payload,
        owner_id: currentUser.id,
        owner_name: ownerLabel || currentUser.agent_name || currentUser.full_name || 'User',
        org_id: currentUser.org_id,
      })
      if (error) { console.error('Create failed:', error); alert('Create failed — ' + error.message); setSaving(false); return }
    }
    setForm({ ...emptyLead })
    setShowAddForm(false)
    setEditingLead(null)
    setSaving(false)
    onRefresh()
  }

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this lead?')) return
    await deleteLead(id)
    onRefresh()
  }

  const handleStatusChange = async (lead, newStatus) => {
    const { error } = await updateLead(lead.id, { status: newStatus })
    if (error) { console.error('Status update failed:', error); alert('Status update failed — ' + error.message); return }
    onRefresh()
  }

  const startEdit = (lead) => {
    setForm({
      brand: lead.brand || '',
      client: lead.client || '',
      contact_name: lead.contact_name || '',
      contact_email: lead.contact_email || '',
      contact_phone: lead.contact_phone || '',
      status: lead.status || 'New Lead',
      priority: lead.priority || 'warm',
      estimated_value: lead.estimated_value || 0,
      notes: lead.notes || '',
      next_follow_up: lead.next_follow_up || '',
      last_contacted: lead.last_contacted || '',
    })
    setEditingLead(lead)
    setShowAddForm(true)
  }

  const priorityIcon = (p) => p === 'hot' ? '🔥' : p === 'cold' ? '❄️' : '☀️'
  const sc = (status) => STATUS_COLORS[status] || STATUS_COLORS['New Lead']

  const toggleSort = (f) => { if (sortField === f) setSortDir(d => d === 'asc' ? 'desc' : 'asc'); else { setSortField(f); setSortDir('desc') } }
  const sortIcon = (f) => sortField === f ? (sortDir === 'asc' ? ' ↑' : ' ↓') : ''

  const thStyle = (f) => ({
    padding: '9px 14px', textAlign: 'left', fontSize: 10, fontWeight: 700,
    color: sortField === f ? 'var(--accent)' : 'var(--text3)', textTransform: 'uppercase',
    letterSpacing: '0.5px', borderBottom: '1px solid var(--border)', cursor: f ? 'pointer' : 'default',
    userSelect: 'none', whiteSpace: 'nowrap',
    background: sortField === f ? 'rgba(139,92,246,0.04)' : 'transparent'
  })
  const tdStyle = { padding: '10px 14px', borderBottom: '1px solid var(--border)', fontSize: 13, verticalAlign: 'middle' }

  // Shared lead form panel. Rendered inline at the top for new leads, and
  // inline beneath a row when editing that lead.
  const renderLeadForm = () => (
    <div style={{
      background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 12,
      padding: isMobile ? 16 : 24, marginBottom: 20, boxShadow: 'var(--shadow-md)'
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div style={{ fontWeight: 700, fontSize: 16, fontFamily: 'var(--font-display)' }}>{editingLead ? 'Edit Lead' : 'New Lead'}</div>
        <button onClick={() => { setShowAddForm(false); setEditingLead(null) }} style={{ background: 'none', border: 'none', color: 'var(--text3)', fontSize: 18, cursor: 'pointer' }}>×</button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr', gap: 12, marginBottom: 12 }}>
        {[
          { key: 'brand', label: 'Brand / Company *', type: 'text' },
          { key: 'client', label: 'Client / Agency', type: 'text' },
          { key: 'contact_name', label: 'Contact Name', type: 'text' },
          { key: 'contact_email', label: 'Email', type: 'email' },
          { key: 'contact_phone', label: 'Phone', type: 'tel' },
          { key: 'estimated_value', label: 'Est. Value ($)', type: 'number' },
        ].map(f => (
          <div key={f.key}>
            <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text3)', display: 'block', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.3px' }}>{f.label}</label>
            <input
              value={form[f.key]} onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))}
              type={f.type} placeholder={f.label.replace(' *', '')}
              style={{
                width: '100%', padding: '8px 12px', borderRadius: 7, border: '1px solid var(--border)',
                background: 'var(--bg)', color: 'var(--text)', fontSize: 13, fontFamily: 'var(--font-body)', outline: 'none', boxSizing: 'border-box'
              }}
            />
          </div>
        ))}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr 1fr', gap: 12, marginBottom: 12 }}>
        <div>
          <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text3)', display: 'block', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.3px' }}>Status</label>
          <select value={form.status} onChange={e => setForm(p => ({ ...p, status: e.target.value }))}
            style={{ width: '100%', padding: '8px 12px', borderRadius: 7, border: '1px solid var(--border)', background: 'var(--bg)', color: 'var(--text)', fontSize: 13, fontFamily: 'var(--font-body)', outline: 'none' }}>
            {LEAD_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div>
          <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text3)', display: 'block', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.3px' }}>Priority</label>
          <select value={form.priority} onChange={e => setForm(p => ({ ...p, priority: e.target.value }))}
            style={{ width: '100%', padding: '8px 12px', borderRadius: 7, border: '1px solid var(--border)', background: 'var(--bg)', color: 'var(--text)', fontSize: 13, fontFamily: 'var(--font-body)', outline: 'none' }}>
            {PRIORITIES.map(p => <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>)}
          </select>
        </div>
        <div>
          <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text3)', display: 'block', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.3px' }}>Last Connect</label>
          <input type="date" value={form.last_contacted} onChange={e => setForm(p => ({ ...p, last_contacted: e.target.value }))}
            onClick={e => { try { e.target.showPicker() } catch {} }}
            style={{ width: '100%', padding: '8px 12px', borderRadius: 7, border: '1px solid var(--border)', background: 'var(--bg)', color: 'var(--text)', fontSize: 13, fontFamily: 'var(--font-body)', outline: 'none', boxSizing: 'border-box', colorScheme: 'dark' }}
          />
        </div>
        <div>
          <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text3)', display: 'block', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.3px' }}>Next Follow-up</label>
          <input type="date" value={form.next_follow_up} onChange={e => setForm(p => ({ ...p, next_follow_up: e.target.value }))}
            onClick={e => { try { e.target.showPicker() } catch {} }}
            style={{ width: '100%', padding: '8px 12px', borderRadius: 7, border: '1px solid var(--border)', background: 'var(--bg)', color: 'var(--text)', fontSize: 13, fontFamily: 'var(--font-body)', outline: 'none', boxSizing: 'border-box', colorScheme: 'dark' }}
          />
        </div>
      </div>
      <div style={{ marginBottom: 16 }}>
        <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text3)', display: 'block', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.3px' }}>Notes</label>
        <textarea value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} rows={3}
          placeholder="Any notes about this lead..."
          style={{
            width: '100%', padding: '8px 12px', borderRadius: 7, border: '1px solid var(--border)',
            background: 'var(--bg)', color: 'var(--text)', fontSize: 13, fontFamily: 'var(--font-body)', outline: 'none', resize: 'vertical', boxSizing: 'border-box'
          }}
        />
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <button onClick={handleSave} disabled={!form.brand.trim() || saving} style={{
          padding: '9px 24px', borderRadius: 8, border: 'none',
          background: form.brand.trim() ? 'linear-gradient(135deg, #8b5cf6, #6366f1)' : 'var(--bg3)',
          color: form.brand.trim() ? '#fff' : 'var(--text3)',
          fontSize: 13, fontWeight: 700, cursor: form.brand.trim() ? 'pointer' : 'default',
          fontFamily: 'var(--font-body)'
        }}>{saving ? 'Saving...' : editingLead ? 'Update Lead' : 'Add Lead'}</button>
        <button onClick={() => { setShowAddForm(false); setEditingLead(null) }} style={{
          padding: '9px 18px', borderRadius: 8, border: '1px solid var(--border)',
          background: 'transparent', color: 'var(--text3)', fontSize: 13, fontWeight: 600,
          cursor: 'pointer', fontFamily: 'var(--font-body)'
        }}>Cancel</button>
      </div>
    </div>
  )

  return (
    <div className="fade-in">
      {/* Header */}
      <div style={{
        background: 'var(--gradient-card)', border: '1px solid var(--card-border)',
        borderRadius: 'var(--radius-lg)', padding: isMobile ? 20 : 32,
        marginBottom: 24, position: 'relative', overflow: 'hidden', boxShadow: 'var(--shadow-sm)'
      }}>
        <div style={{ position: 'absolute', inset: 0, boxShadow: 'var(--shadow-inner)', borderRadius: 'var(--radius-lg)', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', top: -60, right: -60, width: 200, height: 200, borderRadius: '50%', background: 'rgba(139,92,246,0.04)', pointerEvents: 'none' }} />
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center', flexDirection: isMobile ? 'column' : 'row', gap: 16 }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 }}>
              <div style={{
                width: 42, height: 42, borderRadius: 12,
                background: 'linear-gradient(135deg, #8b5cf6, #6366f1)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 18, fontWeight: 700, color: '#fff', fontFamily: 'var(--font-display)',
                boxShadow: '0 4px 12px rgba(139,92,246,0.3)'
              }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z"/></svg>
              </div>
              <div>
                <h1 style={{ fontFamily: 'var(--font-display)', fontSize: isMobile ? 24 : 30, fontWeight: 700, letterSpacing: '-0.6px', lineHeight: 1.1 }}>{ownerLabel}'s CRM</h1>
                <p style={{ color: 'var(--text2)', fontSize: 13, marginTop: 2 }}>Your personal leads and sales pipeline</p>
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ textAlign: 'center', padding: '8px 16px', background: 'var(--bg3)', borderRadius: 10, border: '1px solid var(--border)', minWidth: 65 }}>
              <div style={{ fontSize: 20, fontWeight: 700, fontFamily: 'var(--font-display)', color: '#3b82f6' }}>{activeCount}</div>
              <div style={{ fontSize: 10, color: 'var(--text3)', fontWeight: 500 }}>Active</div>
            </div>
            <div style={{ textAlign: 'center', padding: '8px 16px', background: 'var(--bg3)', borderRadius: 10, border: '1px solid var(--border)', minWidth: 65 }}>
              <div style={{ fontSize: 20, fontWeight: 700, fontFamily: 'var(--font-display)', color: '#10b981' }}>{wonCount}</div>
              <div style={{ fontSize: 10, color: 'var(--text3)', fontWeight: 500 }}>Won</div>
            </div>
            <div style={{ textAlign: 'center', padding: '8px 16px', background: 'var(--bg3)', borderRadius: 10, border: '1px solid var(--border)', minWidth: 65 }}>
              <div style={{ fontSize: 20, fontWeight: 700, fontFamily: 'var(--font-display)', color: hide ? 'var(--text)' : 'var(--accent)' }}>{hide ? '•••' : fmt(totalValue)}</div>
              <div style={{ fontSize: 10, color: 'var(--text3)', fontWeight: 500 }}>Pipeline</div>
            </div>
            {overdueCount > 0 && (
              <div style={{ textAlign: 'center', padding: '8px 16px', background: 'rgba(239,68,68,0.08)', borderRadius: 10, border: '1px solid rgba(239,68,68,0.2)', minWidth: 65 }}>
                <div style={{ fontSize: 20, fontWeight: 700, fontFamily: 'var(--font-display)', color: '#ef4444' }}>{overdueCount}</div>
                <div style={{ fontSize: 10, color: '#ef4444', fontWeight: 500 }}>Overdue</div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <button onClick={() => { setShowAddForm(true); setEditingLead(null); setForm({ ...emptyLead }) }} style={{
          padding: '9px 18px', borderRadius: 8, border: 'none',
          background: 'linear-gradient(135deg, #8b5cf6, #6366f1)',
          color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer',
          fontFamily: 'var(--font-body)', boxShadow: '0 2px 8px rgba(139,92,246,0.3)',
          display: 'flex', alignItems: 'center', gap: 6
        }}>
          + Add Lead
        </button>
        <div style={{ position: 'relative', flex: 1, minWidth: 180 }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--text3)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)' }}><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search leads..."
            style={{ width: '100%', padding: '9px 14px 9px 34px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg3)', color: 'var(--text)', fontSize: 13, fontFamily: 'var(--font-body)', outline: 'none' }}
          />
        </div>
        <div style={{ display: 'flex', gap: 4, background: 'var(--bg3)', borderRadius: 8, border: '1px solid var(--border)', padding: 3 }}>
          {['Active', 'All', 'Won', 'Lost'].map(s => (
            <button key={s} onClick={() => setFilterStatus(s)} style={{
              padding: '5px 12px', borderRadius: 6, border: 'none',
              background: filterStatus === s ? (s === 'Won' ? 'rgba(16,185,129,0.15)' : s === 'Lost' ? 'rgba(239,68,68,0.15)' : 'var(--accent)') : 'transparent',
              color: filterStatus === s ? (s === 'Won' ? '#10b981' : s === 'Lost' ? '#ef4444' : '#fff') : 'var(--text3)',
              fontWeight: 600, fontSize: 11, cursor: 'pointer', fontFamily: 'var(--font-body)'
            }}>{s}</button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 4, background: 'var(--bg3)', borderRadius: 8, border: '1px solid var(--border)', padding: 3 }}>
          {[{ id: 'list', label: 'List' }, { id: 'board', label: 'Board' }].map(v => (
            <button key={v.id} onClick={() => setView(v.id)} style={{
              padding: '5px 10px', borderRadius: 6, border: 'none',
              background: view === v.id ? 'var(--accent)' : 'transparent',
              color: view === v.id ? '#fff' : 'var(--text3)',
              fontWeight: 600, fontSize: 11, cursor: 'pointer', fontFamily: 'var(--font-body)'
            }}>{v.label}</button>
          ))}
        </div>
      </div>

      {/* New-lead form — inline at top. Edits render inline beneath the row instead. */}
      {showAddForm && !editingLead && renderLeadForm()}

      {/* List View — CRM-style table with expandable rows */}
      {view === 'list' && (
        <div style={{
          background: 'var(--bg2)', border: '1px solid var(--border)',
          borderRadius: 'var(--radius-lg)', overflow: 'hidden', boxShadow: 'var(--shadow-sm)', marginBottom: 20
        }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={thStyle('brand')} onClick={() => toggleSort('brand')}>Brand{sortIcon('brand')}</th>
                  <th style={thStyle('client')} onClick={() => toggleSort('client')}>Client{sortIcon('client')}</th>
                  <th style={thStyle('status')} onClick={() => toggleSort('status')}>Status{sortIcon('status')}</th>
                  <th style={thStyle('priority')} onClick={() => toggleSort('priority')}>Priority{sortIcon('priority')}</th>
                  {!hide && <th style={thStyle('estimated_value')} onClick={() => toggleSort('estimated_value')}>Value{sortIcon('estimated_value')}</th>}
                  {!isMobile && <th style={thStyle('contact_name')} onClick={() => toggleSort('contact_name')}>Contact{sortIcon('contact_name')}</th>}
                  {!isMobile && <th style={thStyle('last_contacted')} onClick={() => toggleSort('last_contacted')}>Last Connect{sortIcon('last_contacted')}</th>}
                  {!isMobile && <th style={thStyle('next_follow_up')} onClick={() => toggleSort('next_follow_up')}>Follow-up{sortIcon('next_follow_up')}</th>}
                  <th style={thStyle('created_at')} onClick={() => toggleSort('created_at')}>Added{sortIcon('created_at')}</th>
                </tr>
              </thead>
              {sorted.map(lead => {
                const isExpanded = expandedLead === lead.id
                const sColor = sc(lead.status)
                const fu = daysUntil(lead.next_follow_up)
                const brandHue = (lead.brand || '').charCodeAt(0) * 7 % 360
                return (
                  <tbody key={lead.id}>
                    <tr
                      className="row-hover"
                      onClick={() => setExpandedLead(isExpanded ? null : lead.id)}
                      style={{ cursor: 'pointer', transition: 'background 0.15s' }}
                    >
                      <td style={tdStyle}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <div style={{
                            width: 36, height: 36, borderRadius: 9, flexShrink: 0,
                            background: `hsl(${brandHue}, 35%, 22%)`,
                            border: `1.5px solid hsl(${brandHue}, 45%, 40%)`,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: 11, fontWeight: 700, color: `hsl(${brandHue}, 45%, 65%)`,
                            fontFamily: 'var(--font-display)'
                          }}>
                            {(lead.brand || '??').slice(0, 2).toUpperCase()}
                          </div>
                          <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <span style={{ fontWeight: 600, fontSize: 13 }}>{lead.brand}</span>
                              <span style={{ fontSize: 12 }}>{priorityIcon(lead.priority)}</span>
                              {lead.source === 'agency_crm' && (
                                <span style={{ fontSize: 9, padding: '1px 5px', borderRadius: 4, background: 'rgba(59,130,246,0.12)', color: '#3b82f6', fontWeight: 600 }}>AGENCY</span>
                              )}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td style={{ ...tdStyle, color: 'var(--text2)', fontSize: 12 }}>{hide ? '•••••' : (lead.client || '--')}</td>
                      <td style={tdStyle} onClick={e => e.stopPropagation()}>
                        <select
                          value={lead.status}
                          onChange={e => handleStatusChange(lead, e.target.value)}
                          style={{
                            padding: '4px 8px', borderRadius: 6, fontSize: 11, fontWeight: 600,
                            background: sColor.bg, color: sColor.color, border: `1px solid ${sColor.color}30`,
                            cursor: 'pointer', outline: 'none', fontFamily: 'var(--font-body)'
                          }}
                        >
                          {LEAD_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </td>
                      <td style={tdStyle}>
                        <span style={{
                          padding: '3px 8px', borderRadius: 6, fontSize: 10, fontWeight: 700,
                          background: lead.priority === 'hot' ? 'rgba(239,68,68,0.12)' : lead.priority === 'cold' ? 'rgba(59,130,246,0.12)' : 'rgba(245,158,11,0.12)',
                          color: lead.priority === 'hot' ? '#ef4444' : lead.priority === 'cold' ? '#3b82f6' : '#f59e0b',
                          textTransform: 'uppercase'
                        }}>{lead.priority}</span>
                      </td>
                      {!hide && <td style={{ ...tdStyle, fontWeight: 700, fontFamily: 'var(--font-display)', color: 'var(--accent)' }}>{lead.estimated_value ? fmt(lead.estimated_value) : '--'}</td>}
                      {!isMobile && (
                        <td style={tdStyle}>
                          {lead.contact_name ? (
                            <div>
                              <div style={{ fontWeight: 500, fontSize: 12 }}>{hide ? '•••••' : lead.contact_name}</div>
                              {lead.contact_email && !hide && (
                                <a href={`mailto:${lead.contact_email}`} style={{ fontSize: 10, color: '#3b82f6' }} onClick={e => e.stopPropagation()}>{lead.contact_email}</a>
                              )}
                            </div>
                          ) : '--'}
                        </td>
                      )}
                      {!isMobile && (
                        <td style={{ ...tdStyle, fontSize: 11, color: lead.last_contacted ? 'var(--text2)' : 'var(--text3)' }}>
                          {lead.last_contacted ? timeAgo(lead.last_contacted) : '--'}
                        </td>
                      )}
                      {!isMobile && (
                        <td style={tdStyle}>
                          {fu ? (
                            <span style={{ fontSize: 11, fontWeight: 600, color: fu.overdue ? '#ef4444' : '#3b82f6' }}>{fu.text}</span>
                          ) : '--'}
                        </td>
                      )}
                      <td style={{ ...tdStyle, color: 'var(--text3)', fontSize: 11, whiteSpace: 'nowrap' }}>{timeAgo(lead.created_at)}</td>
                    </tr>

                    {/* Expanded detail row */}
                    {isExpanded && (
                      <tr>
                        <td colSpan={99} style={{ padding: '0 14px 14px', background: 'var(--bg3)', borderBottom: '1px solid var(--border)' }}>
                          <div style={{ padding: 16, background: 'var(--bg2)', borderRadius: 10, border: '1px solid var(--border)', marginTop: 8 }} onClick={e => e.stopPropagation()}>
                            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : '1fr 1fr 1fr 1fr', gap: 12, marginBottom: 12 }}>
                              {lead.contact_email && !hide && (
                                <div>
                                  <div style={{ fontSize: 10, color: 'var(--text3)', textTransform: 'uppercase', fontWeight: 600 }}>Email</div>
                                  <a href={`mailto:${lead.contact_email}`} style={{ fontSize: 12, color: '#3b82f6' }}>{lead.contact_email}</a>
                                </div>
                              )}
                              {lead.contact_phone && !hide && (
                                <div>
                                  <div style={{ fontSize: 10, color: 'var(--text3)', textTransform: 'uppercase', fontWeight: 600 }}>Phone</div>
                                  <a href={`tel:${lead.contact_phone}`} style={{ fontSize: 12, color: '#3b82f6' }}>{lead.contact_phone}</a>
                                </div>
                              )}
                              {lead.last_contacted && (
                                <div>
                                  <div style={{ fontSize: 10, color: 'var(--text3)', textTransform: 'uppercase', fontWeight: 600 }}>Last Contacted</div>
                                  <div style={{ fontSize: 12 }}>{timeAgo(lead.last_contacted)}</div>
                                </div>
                              )}
                              <div>
                                <div style={{ fontSize: 10, color: 'var(--text3)', textTransform: 'uppercase', fontWeight: 600 }}>Added</div>
                                <div style={{ fontSize: 12 }}>{timeAgo(lead.created_at)}</div>
                              </div>
                            </div>
                            {lead.notes && (
                              <div style={{ marginBottom: 12 }}>
                                <div style={{ fontSize: 10, color: 'var(--text3)', textTransform: 'uppercase', fontWeight: 600, marginBottom: 4 }}>Notes</div>
                                <div style={{ fontSize: 12, color: 'var(--text2)', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{lead.notes}</div>
                              </div>
                            )}
                            <div style={{ display: 'flex', gap: 8 }}>
                              <button onClick={() => startEdit(lead)} style={{
                                padding: '6px 14px', borderRadius: 7, border: '1px solid var(--border)',
                                background: 'var(--bg3)', color: 'var(--text)', fontSize: 11, fontWeight: 600,
                                cursor: 'pointer', fontFamily: 'var(--font-body)'
                              }}>Edit</button>
                              {lead.contact_email && !hide && (
                                <a href={`mailto:${lead.contact_email}`} style={{
                                  padding: '6px 14px', borderRadius: 7, border: '1px solid rgba(59,130,246,0.3)',
                                  background: 'rgba(59,130,246,0.1)', color: '#3b82f6', fontSize: 11, fontWeight: 600,
                                  cursor: 'pointer', fontFamily: 'var(--font-body)', textDecoration: 'none',
                                  display: 'inline-flex', alignItems: 'center'
                                }}>Email</a>
                              )}
                              {onCopyLeadToMyCRM && (
                                <button onClick={() => onCopyLeadToMyCRM(lead)} style={{
                                  padding: '6px 14px', borderRadius: 7, border: '1px solid rgba(59,130,246,0.3)',
                                  background: 'rgba(59,130,246,0.1)', color: '#3b82f6', fontSize: 11, fontWeight: 600,
                                  cursor: 'pointer', fontFamily: 'var(--font-body)',
                                  display: 'inline-flex', alignItems: 'center', gap: 4
                                }}>Copy to my CRM</button>
                              )}
                              {onConvertToDeal && lead.status === 'Won' && (
                                isLeadConverted(lead, deals) ? (
                                  <span style={{
                                    padding: '6px 14px', borderRadius: 7, border: '1px solid var(--border)',
                                    background: 'var(--bg3)', color: 'var(--text3)', fontSize: 11, fontWeight: 600,
                                    fontFamily: 'var(--font-body)',
                                    display: 'inline-flex', alignItems: 'center', gap: 4
                                  }}>Converted</span>
                                ) : (
                                  <button onClick={() => onConvertToDeal(lead)} style={{
                                    padding: '6px 14px', borderRadius: 7, border: '1px solid rgba(16,185,129,0.3)',
                                    background: 'rgba(16,185,129,0.1)', color: '#10b981', fontSize: 11, fontWeight: 600,
                                    cursor: 'pointer', fontFamily: 'var(--font-body)',
                                    display: 'inline-flex', alignItems: 'center', gap: 4
                                  }}>Convert to Deal</button>
                                )
                              )}
                              <button onClick={() => handleDelete(lead.id)} style={{
                                padding: '6px 14px', borderRadius: 7, border: '1px solid rgba(239,68,68,0.3)',
                                background: 'rgba(239,68,68,0.08)', color: '#ef4444', fontSize: 11, fontWeight: 600,
                                cursor: 'pointer', fontFamily: 'var(--font-body)', marginLeft: 'auto'
                              }}>Delete</button>
                            </div>
                            {!hide && (
                              <LeadTimeline
                                lead={lead}
                                currentUser={currentUser}
                                onLogged={onRefresh}
                                isMobile={isMobile}
                              />
                            )}
                            {editingLead?.id === lead.id && (
                              <div style={{ marginTop: 14 }}>
                                {renderLeadForm()}
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                )
              })}
              {sorted.length === 0 && (
                <tbody><tr><td colSpan={99} style={{ padding: 48, textAlign: 'center', color: 'var(--text3)' }}>
                  <div style={{ fontSize: 24, marginBottom: 8 }}>No leads yet</div>
                  <div style={{ fontSize: 13 }}>Click "+ Add Lead" to create your first one</div>
                </td></tr></tbody>
              )}
            </table>
          </div>
        </div>
      )}

      {/* Board View */}
      {view === 'board' && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : `repeat(${Math.min(boardGroups.length, 5)}, 1fr)`,
          gap: 12, marginBottom: 20,
          overflowX: isMobile ? 'visible' : 'auto'
        }}>
          {boardGroups.map(group => {
            const sColor = sc(group.status)
            return (
              <div key={group.status} style={{ minWidth: isMobile ? 'auto' : 200 }}>
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '8px 12px', marginBottom: 8,
                  background: sColor.bg, borderRadius: 8, border: `1px solid ${sColor.color}25`
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <div style={{ width: 8, height: 8, borderRadius: '50%', background: sColor.dot }} />
                    <span style={{ fontSize: 12, fontWeight: 700, color: sColor.color }}>{group.status}</span>
                  </div>
                  <span style={{ fontSize: 11, fontWeight: 700, color: sColor.color }}>{group.leads.length}</span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {group.leads.map(lead => {
                    const fu = daysUntil(lead.next_follow_up)
                    return (
                      <div key={lead.id} className="row-hover" style={{
                        background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 10,
                        padding: 12, cursor: 'pointer', transition: 'all 0.15s',
                        borderLeft: `3px solid ${sColor.dot}`
                      }} onClick={() => setExpandedLead(expandedLead === lead.id ? null : lead.id)}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                              <span style={{ fontSize: 12 }}>{priorityIcon(lead.priority)}</span>
                              <span style={{ fontWeight: 700, fontSize: 14 }}>{lead.brand}</span>
                              {lead.source === 'agency_crm' && (
                                <span style={{ fontSize: 9, padding: '1px 5px', borderRadius: 4, background: 'rgba(59,130,246,0.12)', color: '#3b82f6', fontWeight: 600 }}>AGENCY</span>
                              )}
                            </div>
                            {lead.client && <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 2 }}>{hide ? '•••••' : lead.client}</div>}
                            {lead.contact_name && <div style={{ fontSize: 11, color: 'var(--text2)' }}>{hide ? '•••••' : lead.contact_name}</div>}
                          </div>
                          <div style={{ textAlign: 'right', flexShrink: 0 }}>
                            {lead.estimated_value > 0 && (
                              <div style={{ fontWeight: 700, fontFamily: 'var(--font-display)', fontSize: 13, color: 'var(--accent)' }}>
                                {hide ? '•••••' : fmt(lead.estimated_value)}
                              </div>
                            )}
                          </div>
                        </div>
                        {fu && (
                          <div style={{ marginTop: 6, fontSize: 11, fontWeight: 600, color: fu.overdue ? '#ef4444' : '#3b82f6' }}>
                            Follow-up: {fu.text}
                          </div>
                        )}
                        {onCopyLeadToMyCRM && (
                          <button onClick={e => { e.stopPropagation(); onCopyLeadToMyCRM(lead) }} style={{
                            marginTop: 8, width: '100%', padding: '6px 0', borderRadius: 6,
                            border: '1px solid rgba(59,130,246,0.3)', background: 'rgba(59,130,246,0.1)',
                            color: '#3b82f6', fontSize: 11, fontWeight: 700, cursor: 'pointer',
                            fontFamily: 'var(--font-body)'
                          }}>Copy to my CRM</button>
                        )}
                        {onConvertToDeal && lead.status === 'Won' && (
                          isLeadConverted(lead, deals) ? (
                            <div style={{
                              marginTop: 8, width: '100%', padding: '6px 0', borderRadius: 6,
                              border: '1px solid var(--border)', background: 'var(--bg3)',
                              color: 'var(--text3)', fontSize: 11, fontWeight: 700, textAlign: 'center',
                              fontFamily: 'var(--font-body)'
                            }}>Converted</div>
                          ) : (
                            <button onClick={e => { e.stopPropagation(); onConvertToDeal(lead) }} style={{
                              marginTop: 8, width: '100%', padding: '6px 0', borderRadius: 6,
                              border: '1px solid rgba(16,185,129,0.3)', background: 'rgba(16,185,129,0.1)',
                              color: '#10b981', fontSize: 11, fontWeight: 700, cursor: 'pointer',
                              fontFamily: 'var(--font-body)'
                            }}>Convert to Deal</button>
                          )
                        )}
                      </div>
                    )
                  })}
                  {group.leads.length === 0 && (
                    <div style={{ padding: 16, textAlign: 'center', fontSize: 11, color: 'var(--text3)', border: '1px dashed var(--border)', borderRadius: 8 }}>No leads</div>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* Footer */}
      <div style={{ display: 'flex', gap: 20, fontSize: 12, color: 'var(--text3)', flexWrap: 'wrap' }}>
        <span>{filtered.length} leads</span>
        <span>Pipeline: <strong style={{ color: 'var(--text)' }}>{hide ? '•••••' : fmt(totalValue)}</strong></span>
        <span>Won: <strong style={{ color: '#10b981' }}>{wonCount}</strong></span>
      </div>
    </div>
  )
}
